import React, { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, ScrollView, StatusBar, StyleSheet, Text, View } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import Icon from 'react-native-vector-icons/Feather';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useFocusEffect } from '@react-navigation/native';
import { collection, doc, getCountFromServer, getDoc, getDocs, query, where } from 'firebase/firestore';
import { Colors, Radius, Shadow, Typography } from '../theme';
import { auth, FIRESTORE_COLLECTIONS, firestore } from '../firebase';
import { useI18n } from '../i18n';

type EventAnalyticsSummary = {
  eventId: string;
  title: string;
  detailTaps: number;
  sharesCount: number;
  interestedCount: number;
};

export default function PartnerAnalyticsScreen() {
  const insets = useSafeAreaInsets();
  const { t } = useI18n();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState<EventAnalyticsSummary[]>([]);

  const load = useCallback(async () => {
    const uid = auth.currentUser?.uid;
    if (!uid) {
      setEvents([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const activitiesSnap = await getDocs(
        query(collection(firestore, FIRESTORE_COLLECTIONS.activities), where('hostId', '==', uid)),
      );

      const summaries = await Promise.all(activitiesSnap.docs.map(async activityDoc => {
        const data = activityDoc.data() as { title?: string };
        const [analyticsSnap, interestCountSnap] = await Promise.all([
          getDoc(doc(firestore, 'eventAnalytics', activityDoc.id)),
          getCountFromServer(
            query(collection(firestore, FIRESTORE_COLLECTIONS.activityInterests), where('activityId', '==', activityDoc.id)),
          ),
        ]);
        const analytics = analyticsSnap.exists() ? (analyticsSnap.data() as { detailTaps?: number; sharesCount?: number }) : {};
        return {
          eventId: activityDoc.id,
          title: data.title || t('partner.untitledEvent'),
          detailTaps: analytics.detailTaps || 0,
          sharesCount: analytics.sharesCount || 0,
          interestedCount: interestCountSnap.data().count,
        } satisfies EventAnalyticsSummary;
      }));

      setEvents(summaries.sort((a, b) => b.detailTaps - a.detailTaps));
    } catch (error) {
      console.warn('[Tonyte][PartnerAnalytics] failed to load analytics', error);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { void load(); }, [load]);
  useFocusEffect(useCallback(() => { void load(); }, [load]));

  const totals = events.reduce(
    (acc, event) => ({
      taps: acc.taps + event.detailTaps,
      interested: acc.interested + event.interestedCount,
      shares: acc.shares + event.sharesCount,
    }),
    { taps: 0, interested: 0, shares: 0 },
  );

  return (
    <LinearGradient colors={['#0B1826', '#070B14', '#020409']} style={styles.container}>
      <StatusBar barStyle="light-content" translucent backgroundColor="transparent" />
      <ScrollView contentContainerStyle={[styles.scroll, { paddingTop: insets.top + 24, paddingBottom: insets.bottom + 40 }]}>
        <Text style={styles.title}>{t('partner.analytics')}</Text>

        {loading ? (
          <ActivityIndicator size="large" color="#FF7A00" style={styles.loader} />
        ) : (
          <>
            <View style={styles.statsRow}>
              <View style={styles.statCard}>
                <Icon name="eye" size={18} color="#FF7A00" />
                <Text style={styles.statValue}>{totals.taps}</Text>
                <Text style={styles.statLabel}>{t('partner.totalTaps')}</Text>
              </View>
              <View style={styles.statCard}>
                <Icon name="heart" size={18} color="#FF7A00" />
                <Text style={styles.statValue}>{totals.interested}</Text>
                <Text style={styles.statLabel}>{t('partner.interestedTaps')}</Text>
              </View>
              <View style={styles.statCard}>
                <Icon name="share-2" size={18} color="#FF7A00" />
                <Text style={styles.statValue}>{totals.shares}</Text>
                <Text style={styles.statLabel}>{t('partner.shares')}</Text>
              </View>
            </View>

            <Text style={styles.sectionTitle}>{t('partner.byEvent')}</Text>
            {events.length === 0 ? (
              <Text style={styles.emptyText}>{t('partner.noAnalytics')}</Text>
            ) : (
              events.map(event => (
                <View key={event.eventId} style={styles.eventCard}>
                  <Text style={styles.eventTitle} numberOfLines={1}>{event.title}</Text>
                  <View style={styles.eventStatsRow}>
                    <Text style={styles.eventStat}>{event.detailTaps} {t('partner.taps')}</Text>
                    <Text style={styles.eventStat}>{event.interestedCount} {t('partner.interested')}</Text>
                    <Text style={styles.eventStat}>{event.sharesCount} {t('partner.sharesLower')}</Text>
                  </View>
                </View>
              ))
            )}
          </>
        )}
      </ScrollView>
    </LinearGradient>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Colors.bg },
  scroll: { paddingHorizontal: 20 },
  loader: { marginTop: 60 },
  title: {
    color: Colors.textPrimary,
    fontSize: Typography.xxl,
    fontWeight: Typography.extrabold,
    marginBottom: 20,
  },
  statsRow: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 24,
  },
  statCard: {
    flex: 1,
    padding: 14,
    borderRadius: Radius.lg,
    borderWidth: 1,
    borderColor: Colors.border,
    backgroundColor: 'rgba(9,17,29,0.92)',
    alignItems: 'center',
    gap: 6,
    ...Shadow.strong,
  },
  statValue: {
    color: Colors.textPrimary,
    fontSize: Typography.xl,
    fontWeight: Typography.extrabold,
  },
  statLabel: {
    color: Colors.textMuted,
    fontSize: Typography.xs,
    textAlign: 'center',
  },
  sectionTitle: {
    color: Colors.textPrimary,
    fontSize: Typography.lg,
    fontWeight: Typography.bold,
    marginBottom: 12,
  },
  emptyText: {
    color: Colors.textMuted,
    fontSize: Typography.sm,
    lineHeight: 21,
  },
  eventCard: {
    padding: 14,
    borderRadius: Radius.lg,
    borderWidth: 1,
    borderColor: Colors.border,
    backgroundColor: 'rgba(9,17,29,0.92)',
    marginBottom: 10,
  },
  eventTitle: {
    color: Colors.textPrimary,
    fontSize: Typography.base,
    fontWeight: Typography.semibold,
    marginBottom: 8,
  },
  eventStatsRow: {
    flexDirection: 'row',
    gap: 14,
  },
  eventStat: {
    color: Colors.textMuted,
    fontSize: Typography.xs,
  },
});
